/**
 * Date/time utilities used by the OnboardingWizard.
 *
 *   - `parseMDY` — convert "MM/DD/YYYY" to ISO "YYYY-MM-DD". Returns null
 *     for any invalid input (bad format, impossible date, future, etc.).
 *   - `isValidTime` — strict "HH:MM" 24-hour validator (00..23 : 00..59).
 *   - `ageFromDOB` — Global Rule 6+7 (DOB wins over typed age). Accepts
 *     either MM/DD/YYYY (wizard) or ISO YYYY-MM-DD (legacy /edit format).
 *   - `resolveAge` — convenience wrapper that returns the value to persist.
 */

const MDY_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse "MM/DD/YYYY" and return ISO "YYYY-MM-DD" if valid, else null.
 * Validates month range (1-12), day-of-month (respecting leap years), and
 * a sane year window (1900..currentYear). Future dates are rejected so the
 * age computation cannot produce negative values.
 */
export function parseMDY(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(MDY_RE);
  if (!m) return null;
  const mm = parseInt(m[1], 10);
  const dd = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return null;
  if (dd < 1 || dd > 31) return null;
  const nowYear = new Date().getFullYear();
  if (yyyy < 1900 || yyyy > nowYear) return null;
  // Construct and re-validate (catches Feb 30, leap years, etc.)
  const d = new Date(yyyy, mm - 1, dd);
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }
  if (d > new Date()) return null;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

/** Strict "HH:MM" 24-hour validator. */
export function isValidTime(s) {
  if (!s || typeof s !== "string") return false;
  const m = s.match(TIME_RE);
  if (!m) return false;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  return h >= 0 && h <= 23 && mi >= 0 && mi <= 59;
}

/**
 * Age from DOB. Accepts MM/DD/YYYY (wizard) or ISO YYYY-MM-DD (legacy).
 * Returns integer years or null if unparseable / future / >130.
 */
export function ageFromDOB(dob) {
  if (!dob) return null;
  let iso = dob;
  if (MDY_RE.test(dob)) iso = parseMDY(dob);
  else if (!ISO_RE.test(dob)) return null;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  if (d > now) return null;
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  if (years < 0 || years > 130) return null;
  return years;
}

/**
 * Returns the value to persist for `age`.
 *  - DOB present + parseable -> recalculated age (overwrites typedAge).
 *  - DOB absent / invalid    -> typedAge as-is (user-controlled).
 */
export function resolveAge(dob, typedAge) {
  const fromDob = ageFromDOB(dob);
  if (fromDob !== null) return fromDob;
  if (typedAge === "" || typedAge === null || typedAge === undefined) return null;
  const n = parseInt(typedAge, 10);
  return Number.isFinite(n) ? n : null;
}
